import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    Table,
    notification,
    Tooltip,
    Pagination,
    Input,
    Typography,
    Modal,
    Form,
    Select,
    Button,
    Tag
} from "antd";
import axios from "axios";
import {
    FlagOutlined
} from "@ant-design/icons";
import { baseUrl } from "../../config";
import { skeletonData } from '../../components/skeletons/Report';
import moment from "moment";
import { Link } from "react-router-dom";
import { useSidebar } from "../../SidebarContext";

const { Search } = Input;
const { Title } = Typography;
const { Option } = Select;

// table code start
const columns = [
    {
        title: "#",
        dataIndex: "index",
        key: "index",
    },
    {
        title: "Idea",
        dataIndex: "idea",
        key: "idea",
    },
    {
        title: "Report By",
        dataIndex: "actionByName",
        key: "actionByName",
    },
    {
        title: "Report On",
        dataIndex: "actionOnName",
        key: "actionOnName",
    },
    {
        title: "Reported Topic",
        dataIndex: "message",
        key: "message"
    },
    {
        title: "Reason Title",
        dataIndex: "reasonTitle",
        key: "reasonTitle"
    },
    {
        title: "Reason Description",
        dataIndex: "reasonDescription",
        key: "reasonDescription"
    },
    {
        title: "Message",
        dataIndex: "description",
        key: "description"
    },
    {
        title: "Report Date",
        dataIndex: "createdAt",
        key: "createdAt",
    },
    {
        title: "Action",
        dataIndex: "action",
        key: "action",
    },
];

function ReportList() {
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const { setReportCount } = useSidebar();
    const [ideaModalVisible, setIdeaModalVisible] = useState(false);
    const [selectedIdea, setSelectedIdea] = useState(null);

    const [flagModalVisible, setFlagModalVisible] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [categories, setCategories] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);


    useEffect(() => {
        readReportUs();
        fetchCategories();
    }, [])

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/config`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
            });

            if (response.status === 200) {
                const reports = response.data.result?.report || [];
                setCategories(reports);
            }
        } catch (error) {
            console.error("Failed to fetch categories", error);
        }
    };

    const handleCategoryChange = (val) => {
        const selectedCat = categories.find(c => c.key === val);
        setReasons(selectedCat ? selectedCat.sub_category || [] : []);
        form.setFieldsValue({ reason: undefined });
    };

    const readReportUs = async () => {
        try {
            const response = await axios.patch(`${baseUrl}/api/user/report`, {}, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`, // Include access token in headers
                },
            });
            if (response.status === 200) {
                setReportCount(0)
            } else {
                console.error(response?.data?.message)
            }
        } catch (error) {
            console.error(error)
        }
    };

    useEffect(() => {
        getContactList();
    }, [searchTerm, currentPage, pageSize]); // Update data when page or page size changes

    async function getContactList() {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                items: pageSize,
            };

            // Check if search is not null or empty, then add it to params
            if (searchTerm && searchTerm.trim() !== '') {
                params.q = searchTerm;
                // params.fields = 'name,email'; // Specify searchable fields
            }

            const response = await axios.get(`${baseUrl}/api/user/report`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`, // Include access token in headers
                },
                params
            });
            if (response.data.success) {
                setUserList(response.data.result);
                setTotalUsers(response.data.pagination.count);
            } else {
                setUserList([])
            }
        } catch (error) {
            notification.info({
                message: 'Info',
                description: error.response?.data?.message,
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    }

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleSearch = (value) => {
        if (value.length >= 3 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
        }
    };

    const handleFlagAction = (report) => {
        setSelectedReport(report);
        setFlagModalVisible(true);
        form.resetFields();
    };

    const onFlagSubmit = async (values) => {
        try {
            setSubmitting(true);
            const payload = {
                userId: values.user,
                category: values.category,
                reason: values.reason,
                reportId: selectedReport._id
            };

            const response = await axios.post(`${baseUrl}/api/user/report-action`, payload, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });

            if (response.data.success) {
                notification.success({
                    message: 'Success',
                    description: 'User blocked and notification sent successfully.',
                });
                setFlagModalVisible(false);
                getContactList();
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message,
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.message || 'Something went wrong',
            });
        } finally {
            setSubmitting(false);
        }
    };

    function formatTitle(str) {
        if (!str) return '-';
        return str
            .split('_')                // split by underscore
            .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize first letter
            .join(' ');                // join back with spaces
    }

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title="Reports"
                            extra={<Search placeholder="Search by report by or report on" onSearch={handleSearch} allowClear />}
                        >
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={loading ? skeletonData : userList.map((user, index) => ({
                                        index: (currentPage - 1) * pageSize + index + 1,
                                        idea: (
                                            <span
                                                style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setSelectedIdea(user.ideaId);
                                                    setIdeaModalVisible(true);
                                                }}
                                            >
                                                {user?.ideaId?.title}
                                            </span>
                                        ),
                                        actionByName: (
                                            <Link to={`/users/${user.actionBy._id}`}>
                                                <div className="avatar-info">
                                                    <Title level={5}>{user?.actionBy?.firstName || ''} {user?.actionBy?.lastName || ''}</Title>
                                                    <p style={{ "textDecoration": "underline" }}>{user.actionBy.email}</p>
                                                </div>
                                            </Link>

                                        ),
                                        actionOnName: (
                                            <Link to={`/users/${user.actionOn._id}`}>
                                                <div className="avatar-info">
                                                    <Title level={5}>{user?.actionOn?.firstName || ''} {user?.actionOn?.lastName || ''}</Title>
                                                    <p style={{ "textDecoration": "underline" }}>{user.actionOn.email}</p>
                                                </div>
                                            </Link>
                                        ),
                                        message: (
                                            <Tooltip title={formatTitle(user.category)}> {/* Full message shown on hover */}
                                                <p style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "200px" // Adjust width as needed
                                                }}>
                                                    {user.category ? formatTitle(user.category) : '-'}
                                                </p>
                                            </Tooltip>
                                        ),
                                        reasonTitle: (
                                            <Tooltip title={formatTitle(user.reasonTitle)}> {/* Full message shown on hover */}
                                                <p style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "200px" // Adjust width as needed
                                                }}>
                                                    {user.reasonTitle ? formatTitle(user.reasonTitle) : '-'}
                                                </p>
                                            </Tooltip>
                                        ),
                                        reasonDescription: (
                                            <Tooltip title={user.reasonDescription}> {/* Full message shown on hover */}
                                                <p style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "200px" // Adjust width as needed
                                                }}>
                                                    {user.reasonDescription ? user.reasonDescription : '-'}
                                                </p>
                                            </Tooltip>
                                        ),
                                        description: (
                                            <Tooltip title={user.message}> {/* Full message shown on hover */}
                                                <p style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "200px" // Adjust width as needed
                                                }}>
                                                    {user.message ? user.message : '-'}
                                                </p>
                                            </Tooltip>
                                        ),
                                        createdAt: (
                                            <p>{moment(user.createdAt).format("MMMM DD, YYYY hh:mm A")}</p>
                                        ),
                                        action: (
                                            <div>
                                                {user.isActionTaken && (
                                                    <Tag color="green" style={{ marginBottom: 8 }}>Mail Sent</Tag>
                                                )}
                                                <Button
                                                    type="link"
                                                    icon={<FlagOutlined style={{ color: 'red' }} />}
                                                    onClick={() => handleFlagAction(user)}
                                                />
                                            </div>
                                        )
                                    }))}
                                    pagination={false} // Disable default pagination inside the table
                                />
                            </div>
                            {/* Keep pagination outside the scrollable div */}
                            <div className="pagination-container">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={totalUsers}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Modal
                title="Idea Details"
                visible={ideaModalVisible}
                onCancel={() => setIdeaModalVisible(false)}
                footer={null}
            >
                {selectedIdea && (
                    <div>
                        <Title level={5}>{selectedIdea.title}</Title>
                        <p><strong>Summary:</strong> {selectedIdea.summary}</p>
                        <div>
                            <p dangerouslySetInnerHTML={{ __html: selectedIdea.description }}></p>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Flag Modal */}
            <Modal
                title="Flag User"
                visible={flagModalVisible}
                onCancel={() => setFlagModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={submitting}
                okText="Submit"
            >
                <Form form={form} layout="vertical" onFinish={onFlagSubmit}>
                    <Form.Item
                        name="user"
                        label="Select User"
                        rules={[{ required: true, message: 'Please select a user' }]}
                    >
                        <Select placeholder="Select a user">
                            {selectedReport && (
                                <>
                                    <Option value={selectedReport.actionOn._id}>
                                        Reported User: {selectedReport.actionOn.firstName} {selectedReport.actionOn.lastName} ({selectedReport.actionOn.email})
                                    </Option>
                                    <Option value={selectedReport.actionBy._id}>
                                        Reporting User: {selectedReport.actionBy.firstName} {selectedReport.actionBy.lastName} ({selectedReport.actionBy.email})
                                    </Option>
                                </>
                            )}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Category"
                        rules={[{ required: true, message: 'Please select a category' }]}
                    >
                        <Select placeholder="Select a category" onChange={handleCategoryChange}>
                            {categories.map(cat => (
                                <Option key={cat.key} value={cat.key}>{cat.category}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="Reason"
                        rules={[{ required: true, message: 'Please select a reason' }]}
                    >
                        <Select placeholder="Select a reason" disabled={!reasons.length}>
                            {reasons.map(r => (
                                <Option key={r.key} value={r.label}>{r.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

export default ReportList;
